import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Lock, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { useT } from '@/i18n';
import { Avatar, Ltr, Wordmark } from '@/components/ui';
import { setLocalePref, setThemePref } from '@/lib/prefs';
import { nextTheme, usePrefs } from '@/store/prefs';
import { useMembership, useSession } from '@/store/session';
import { useCrumbStore } from '@/store/crumb';
import { useReadOnly } from '@/store/readonly';
import { can } from '@/lib/constants';
import { logInfo } from '@/lib/log';
import { NAV, NAV_ITEMS, navIdFor } from './nav';

function ThemeButton() {
  const { t } = useT();
  const theme = usePrefs((s) => s.theme);
  const Icon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const mode = t(theme === 'dark' ? 'themeDark' : theme === 'light' ? 'themeLight' : 'themeSystem');
  const label = t('themeTip', { mode });
  return (
    <button type="button" className="btn btn-icon" title={label} aria-label={label} onClick={() => void setThemePref(nextTheme(theme))}>
      <Icon size={16} aria-hidden />
    </button>
  );
}

export function LanguageSwitch() {
  const { t, locale } = useT();
  return (
    <div role="radiogroup" aria-label={t('language')} className="segmented" dir="ltr">
      <button type="button" role="radio" aria-checked={locale === 'en'} lang="en" onClick={() => void setLocalePref('en')}>EN</button>
      <button type="button" role="radio" aria-checked={locale === 'he'} lang="he" onClick={() => void setLocalePref('he')}>עב</button>
    </div>
  );
}

function AccountMenu() {
  const { t, td } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, memberships, workspaceId, setWorkspace, logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  if (!user) return null;
  const switchTo = (id: string) => {
    if (id !== workspaceId) {
      logInfo('auth', `switching workspace to ${id}`);
      setWorkspace(id);
      qc.removeQueries({ queryKey: ['ws'] });
      navigate('/');
    }
    setOpen(false);
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="btn btn-ghost btn-icon" style={{ border: 'none', borderRadius: '50%' }} aria-haspopup="menu" aria-expanded={open} aria-label={t('account')} onClick={() => setOpen((o) => !o)}>
        <Avatar name={user.name} />
      </button>
      {open ? (
        <div className="menu" role="menu">
          <div style={{ padding: '6px 10px 8px' }} className="stack">
            <strong>{user.name}</strong>
            <Ltr className="muted small">{user.email}</Ltr>
          </div>
          {memberships.length > 1 ? (
            <>
              <span className="label-caps" style={{ padding: '6px 10px 2px' }}>{t('switchWorkspace')}</span>
              {memberships.map((m) => (
                <button key={m.workspaceId} type="button" role="menuitemradio" aria-checked={m.workspaceId === workspaceId} className="menu-item" onClick={() => switchTo(m.workspaceId)}>
                  <span style={{ width: 14 }}>{m.workspaceId === workspaceId ? <Check size={14} aria-hidden /> : null}</span>
                  <span className="ellipsis" style={{ flex: 1 }}>{m.name}</span>
                  <span className="muted small">{td(`role_${m.role}`, m.role)}</span>
                </button>
              ))}
            </>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              logout();
              qc.clear();
              navigate('/login');
            }}
          >
            <LogOut size={14} aria-hidden />
            {t('logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Header() {
  const { t } = useT();
  const location = useLocation();
  const membership = useMembership();
  const user = useSession((s) => s.user);
  const crumb = useCrumbStore((s) => s.crumb);
  const navItem = NAV_ITEMS.find((n) => n.id === navIdFor(location.pathname));
  const crumbText = crumb ?? (navItem ? t(navItem.label) : '');
  return (
    <header className="shell-header">
      <NavLink to="/" aria-label={t('nHome')} className="wordmark-link">
        <Wordmark size={16} />
      </NavLink>
      <span aria-hidden style={{ color: 'var(--line-2)' }}>/</span>
      <span className="hide-narrow" style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>{membership?.name}</span>
      {crumbText ? <span className="muted ellipsis" style={{ fontSize: 13 }}>/ {crumbText}</span> : null}
      <div className="row push" style={{ gap: 10, flex: 'none' }}>
        <div className="searchbox" title={t('searchSoon')} aria-disabled="true">
          <Search size={15} aria-hidden style={{ flex: 'none' }} />
          <span className="ellipsis" style={{ flex: 1 }}>{t('search')}</span>
          <span className="kbd" dir="ltr">⌘K</span>
        </div>
        <div className="row hide-narrow" style={{ gap: 6 }} title={t('presenceSoon')}>
          {user ? <span className="avatar-stack"><Avatar name={user.name} size={26} /></span> : null}
          <span className="muted small" style={{ whiteSpace: 'nowrap' }}>{t('online', { n: 1 })}</span>
        </div>
        <ThemeButton />
        <LanguageSwitch />
        <AccountMenu />
      </div>
    </header>
  );
}

function Sidebar() {
  const { t } = useT();
  const location = useLocation();
  const current = navIdFor(location.pathname);
  return (
    <nav className="shell-nav" aria-label={t('mainNav')}>
      {NAV.map((g) => (
        <div key={g.group} style={{ display: 'grid', gap: 1 }}>
          <span className="label-caps nav-group-label" style={{ padding: '4px 12px 6px' }}>{t(g.group)}</span>
          {g.items.map((n) => {
            const Icon = n.icon;
            const on = current === n.id;
            return (
              <NavLink key={n.id} to={n.to} className="nav-item" aria-current={on ? 'page' : undefined} title={t(n.label)}>
                <Icon size={17} aria-hidden style={{ flex: 'none' }} />
                <span className="nav-label" style={{ flex: 1 }}>{t(n.label)}</span>
                {n.soon ? <span className="nav-soon">{n.soon}</span> : null}
              </NavLink>
            );
          })}
        </div>
      ))}
      <div className="nav-foot" style={{ marginTop: 'auto', padding: 12, border: '1px solid var(--line)', borderRadius: 8, display: 'grid', gap: 6, fontSize: 12, lineHeight: '17px' }}>
        <div className="row" style={{ gap: 6, fontWeight: 700 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }} />
          {t('aiMode')}
        </div>
        <span className="muted">{t('aiModeSub')}</span>
      </div>
    </nav>
  );
}

/**
 * Read-only banner (plan §13.2): shown once a write was refused because the workspace is suspended or pending deletion.
 * A billing suspension (amendment 15) links owners to /billing, where paying restores the workspace.
 */
function ReadOnlyBanner() {
  const { t } = useT();
  const navigate = useNavigate();
  const ro = useReadOnly();
  const workspaceId = useSession((s) => s.workspaceId);
  const membership = useMembership();
  const { pathname } = useLocation();
  // A refused write is the freshest signal; otherwise the profile's membership says so on load.
  const state =
    ro.code && ro.workspaceId === workspaceId
      ? { code: ro.code, billing: ro.billing }
      : membership?.pendingDeletion
        ? { code: 'workspace_pending_deletion' as const, billing: false }
        : membership?.suspended
          ? { code: 'workspace_suspended' as const, billing: membership.suspended.billing }
          : null;
  if (!state) return null;
  // The Billing page shows its own unpaid-invoice notice next to the payment form.
  if (state.billing && pathname.startsWith('/billing')) return null;
  const canPay = state.billing && can(membership?.role, 'billing.manage');
  const text = state.code === 'workspace_pending_deletion' ? t('roPendingDeletion') : state.billing ? (canPay ? t('roBillingOwner') : t('roBillingMember')) : t('roSuspended');
  return (
    <div className="alert alert-error row" role="alert" style={{ alignItems: 'center', margin: '12px 18px 0' }}>
      <Lock size={16} aria-hidden style={{ flex: 'none' }} />
      <span style={{ flex: 1 }}>{text}</span>
      {canPay ? (
        <button type="button" className="btn btn-sm" onClick={() => navigate('/billing')}>
          {t('roPayNow')}
        </button>
      ) : null}
    </div>
  );
}

export function AppShell() {
  const { t } = useT();
  return (
    <div className="shell">
      <a href="#main" className="skip-link">{t('skipToContent')}</a>
      <Header />
      <div className="shell-body">
        <Sidebar />
        <main id="main" className="shell-main" tabIndex={-1}>
          <ReadOnlyBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
